import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Button, Grid, Tabs, Tab,
  TextField, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Link,
  FormControl, InputLabel, Select, MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useIntl } from "react-intl";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

import {
  fetchJob,
  deleteJob,
  updateJob,
  fetchTasks,
} from "../../api/companyApi";
import { fetchRoles } from "../../api/marketApi";
import { COMPANY_ID } from "../../config/appConfig";
import { wrapTextCell } from "../../utils/gridUtils";
import { useNotification } from "../../providers/NotificationProvider";
import DetailSection from "../../components/common/DetailSection";
import DetailField from "../../components/common/DetailField";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import DraggablePaper from "../../components/common/DraggablePaper";

const getLang = () => (window.navigator.language.startsWith("ja") ? "ja" : "en");

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

export default function JobDetailPage() {
  const { businessDomainId, jobId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [data, setData] = useState(null);
  const [tabIndex, setTabIndex] = useState(location.state?.tab ?? 0);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // ── Market Roles (for Japanese name display) ──
  const [marketRoles, setMarketRoles] = useState([]);

  // ── Job KPI state ──
  const [kpiDialogOpen, setKpiDialogOpen] = useState(false);
  const [kpiEditMode, setKpiEditMode] = useState(false);
  const [kpiOriginalName, setKpiOriginalName] = useState(null);
  const [kpiForm, setKpiForm] = useState({
    indicatorName: "", unit: "", measuringMethod: "", evaluationStandard: "", description: "",
  });
  const [kpiConfirmOpen, setKpiConfirmOpen] = useState(false);
  const [kpiDeleteTarget, setKpiDeleteTarget] = useState(null);

  // ── Tasks state ──
  const [tasks, setTasks] = useState([]);

  const loadData = useCallback(async () => {
    try { setData(await fetchJob(jobId)); }
    catch (e) { notify.apiError(e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const loadTasks = useCallback(async () => {
    try { setTasks(await fetchTasks(jobId)); }
    catch (e) { notify.apiError(e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { loadTasks(); }, [loadTasks]);
  useEffect(() => {
    (async () => {
      try {
        const roles = await fetchRoles();
        setMarketRoles(Array.isArray(roles) ? roles : []);
      } catch (e) { /* market API unavailable — fall back to stored roleName */ }
    })();
  }, []);

  // ── Delete job ──
  const handleDelete = async () => {
    try {
      await deleteJob(jobId);
      notify.success(t("message.delete.success"));
      setConfirmOpen(false);
      navigate(`/design/${businessDomainId}/jobs`, { replace: true });
    } catch (e) { notify.apiError(e); }
  };

  // ── Job KPI CRUD ──
  const openKpiAdd = () => {
    setKpiEditMode(false);
    setKpiOriginalName(null);
    setKpiForm({ indicatorName: "", unit: "", measuringMethod: "", evaluationStandard: "", description: "" });
    setKpiDialogOpen(true);
  };
  const openKpiEdit = (row) => {
    setKpiEditMode(true);
    setKpiOriginalName(row.indicatorName);
    setKpiForm({
      indicatorName: row.indicatorName,
      unit: row.unit || "",
      measuringMethod: row.measuringMethod || "",
      evaluationStandard: row.evaluationStandard || "",
      description: row.description || "",
    });
    setKpiDialogOpen(true);
  };
  const handleKpiSave = async () => {
    if (!kpiForm.indicatorName) { notify.error(t("message.error.required")); return; }
    if (!kpiForm.unit) { notify.error(t("message.error.required.input")); return; }
    const indicators = data.indicators || [];
    if (!kpiEditMode || kpiOriginalName !== kpiForm.indicatorName) {
      if (indicators.some((ind) => ind.indicatorName === kpiForm.indicatorName)) {
        notify.error(t("jobKpi.error.duplicate")); return;
      }
    }
    const updatedIndicators = kpiEditMode
      ? indicators.map((ind) => ind.indicatorName === kpiOriginalName ? { ...kpiForm } : ind)
      : [...indicators, { ...kpiForm }];
    // unit が null/空の既存指標はサーバーエラーになるため "-" で補完する
    const safeIndicators = updatedIndicators.map((ind) => ({
      ...ind,
      unit: ind.unit || "-",
    }));
    try {
      await updateJob({ ...data, indicators: safeIndicators });
      notify.success(kpiEditMode ? t("message.update.success") : t("message.create.success"));
      setKpiDialogOpen(false);
      loadData();
    } catch (e) { notify.apiError(e); }
  };
  const handleKpiDelete = async () => {
    const updatedIndicators = (data.indicators || []).filter((ind) => ind.indicatorName !== kpiDeleteTarget);
    try {
      await updateJob({ ...data, indicators: updatedIndicators });
      notify.success(t("message.delete.success"));
      loadData();
    } catch (e) { notify.apiError(e); }
    setKpiConfirmOpen(false); setKpiDeleteTarget(null);
  };

  if (!data) return null;

  const indicators = data.indicators || [];

  // ── Column definitions ──
  const kpiColumns = [
    { field: "indicatorName", headerName: t("appKpi.indicatorName"), flex: 1, minWidth: 160 },
    { field: "unit", headerName: t("appKpi.unit"), width: 100 },
    { field: "measuringMethod", headerName: t("appKpi.measuringMethod"), flex: 1, minWidth: 140, renderCell: wrapTextCell },
    { field: "evaluationStandard", headerName: t("appKpi.evaluationStandard"), flex: 1, minWidth: 140, renderCell: wrapTextCell },
    {
      field: "actions", headerName: t("common.actions"), width: 110, sortable: false,
      renderCell: (p) => (
        <Box>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); openKpiEdit(p.row); }}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={(e) => {
            e.stopPropagation(); setKpiDeleteTarget(p.row.indicatorName); setKpiConfirmOpen(true);
          }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const taskColumns = [
    { field: "taskName", headerName: t("task.name"), flex: 1, minWidth: 180 },
    { field: "taskDescription", headerName: t("task.description"), flex: 1, minWidth: 180, renderCell: wrapTextCell },
    { field: "functionName", headerName: t("task.function"), width: 160 },
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("job.detail.title")}</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="contained" startIcon={<EditIcon />} onClick={() => navigate(`/design/${businessDomainId}/jobs/${jobId}/edit`)}>{t("common.edit")}</Button>
          <Button variant="contained" color="error" startIcon={<DeleteIcon />} onClick={() => setConfirmOpen(true)}>{t("common.delete")}</Button>
        </Box>
      </Box>

      <DetailSection>
        <Grid container spacing={2}>
          <Grid size={12}><DetailField label={t("job.name")} value={data.jobName} /></Grid>
          <Grid size={12}><DetailField label={t("job.description")} value={data.jobDescription} /></Grid>
          <Grid size={12}>
            <DetailField
              label={t("job.role")}
              value={(data.roles || []).map((r) => {
                const mRole = marketRoles.find((mr) => String(mr.id) === String(r.roleId));
                if (mRole) return getLang() === "ja" ? mRole.japaneseName : mRole.englishName;
                return r.roleName || String(r.roleId);
              }).join(", ")}
            />
          </Grid>
          {data.url && (
            <Grid size={12}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{t("common.url")}</Typography>
              <Link href={data.url} target="_blank" rel="noopener noreferrer">{data.url}</Link>
            </Grid>
          )}
        </Grid>
      </DetailSection>

      <Box sx={{ mt: 3, borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)}>
          <Tab label={t("jobKpi.title")} />
          <Tab label={t("task.title")} />
        </Tabs>
      </Box>

      {/* Tab 0: Job KPI */}
      <TabPanel value={tabIndex} index={0}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="subtitle1">{t("jobKpi.list.title")}</Typography>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openKpiAdd}>
            {t("common.add")}
          </Button>
        </Box>
        <DataGrid
          rows={indicators}
          columns={kpiColumns}
          getRowId={(r) => r.indicatorName}
          key={indicators.map((i) => i.indicatorName).join(",")}
          autoHeight pageSizeOptions={[5, 10]}
          initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
          disableRowSelectionOnClick
          getRowHeight={() => 'auto'}
        />
      </TabPanel>

      {/* Tab 1: Tasks */}
      <TabPanel value={tabIndex} index={1}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="subtitle1">{t("task.list.title")}</Typography>
          <Button
            variant="contained" size="small" startIcon={<AddIcon />}
            onClick={() => navigate(`/design/${businessDomainId}/jobs/${jobId}/tasks/create`)}
          >
            {t("common.add")}
          </Button>
        </Box>
        <DataGrid
          rows={tasks}
          columns={taskColumns}
          getRowId={(r) => r.taskId}
          key={tasks.map((tk) => tk.taskId).join(",")}
          autoHeight pageSizeOptions={[5, 10]}
          initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
          onRowClick={(params) => navigate(`/design/${businessDomainId}/jobs/${jobId}/tasks/${params.row.taskId}`)}
          sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
          getRowHeight={() => 'auto'}
        />
      </TabPanel>

      <Box sx={{ mt: 3 }}>
        <Button variant="outlined" onClick={() => navigate(`/design/${businessDomainId}/jobs`)}>{t("common.back")}</Button>
      </Box>

      {/* Confirm Dialogs */}
      <ConfirmDialog open={confirmOpen} onConfirm={handleDelete} onCancel={() => setConfirmOpen(false)} />
      <ConfirmDialog open={kpiConfirmOpen} onConfirm={handleKpiDelete} onCancel={() => { setKpiConfirmOpen(false); setKpiDeleteTarget(null); }} />

      {/* Job KPI Add/Edit Dialog */}
      <Dialog open={kpiDialogOpen} onClose={() => setKpiDialogOpen(false)} maxWidth="sm" fullWidth PaperComponent={DraggablePaper}>
        <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>{kpiEditMode ? t("jobKpi.edit.title") : t("jobKpi.create.title")}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField fullWidth required label={t("appKpi.indicatorName")} value={kpiForm.indicatorName}
              onChange={(e) => setKpiForm({ ...kpiForm, indicatorName: e.target.value })} />
            <TextField fullWidth required label={t("appKpi.unit")} value={kpiForm.unit}
              onChange={(e) => setKpiForm({ ...kpiForm, unit: e.target.value })} />
            <TextField fullWidth multiline rows={2} label={t("appKpi.measuringMethod")} value={kpiForm.measuringMethod}
              onChange={(e) => setKpiForm({ ...kpiForm, measuringMethod: e.target.value })} />
            <TextField fullWidth multiline rows={2} label={t("appKpi.evaluationStandard")} value={kpiForm.evaluationStandard}
              onChange={(e) => setKpiForm({ ...kpiForm, evaluationStandard: e.target.value })} />
            <TextField fullWidth multiline rows={2} label={t("appKpi.description")} value={kpiForm.description}
              onChange={(e) => setKpiForm({ ...kpiForm, description: e.target.value })} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKpiDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={handleKpiSave}>
            {kpiEditMode ? t("common.save") : t("common.add")}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
